import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import Select from "react-select";

import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { fetchDataRead } from "../../../Utility"; // kept for consistency, even if unused

const percentPattern = /^(\d{0,3})(\.\d{0,3})?$/; // up to 3 digits, 3 decimals
const amountPattern = /^(\d{0,12})(\.\d{0,2})?$/; // up to 12 digits, 2 decimals

const toFixedOrEmpty = (val, digits) => {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (Number.isNaN(n)) return "";
  return n.toFixed(digits);
};

const AddTDSMaster = () => {
  const routeLocation = useLocation();
  const [editId, setEditId] = useState(null);

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [accountOptions, setAccountOptions] = useState([]);

  const [form, setForm] = useState({
    account_code: "",
    effective_date: "",
    firm_tds_percent: "",
    individual_tds_percent: "",
    without_pan_tds_percent: "",
    interest_percent: "",
    single_limit_amount: "",
    aggregate_limit_amount: "",
  });

  const [selectedAccountMasterId, setSelectedAccountMasterId] = useState(null);

  // ✅ NEW (minimal): store original edit values so we know if user changed account/date
  const originalEffectiveDateRef = useRef("");
  const originalAccountMasterIdRef = useRef(null);

  const accountSelectOptions = useMemo(
    () =>
      (accountOptions || []).map((o) => {
        const full = o.name_on_account || "";
        const [code, ...rest] = full.split(" ");
        const namePart = rest.join(" ");
        return {
          value: full,
          label: full,
          account_master_id: o.account_master_id,
          _code: (code || "").toLowerCase(),
          _name: (namePart || "").toLowerCase(),
        };
      }),
    [accountOptions]
  );

  const accountFilter = (option, rawInput) => {
    const q = rawInput.trim().toLowerCase();
    if (!q) return true;
    return (
      option.label.toLowerCase().includes(q) ||
      option.data._code.includes(q) ||
      option.data._name.includes(q)
    );
  };

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: 38, boxShadow: "none" }),
    valueContainer: (base) => ({ ...base, paddingTop: 2, paddingBottom: 2 }),
  };

  // helper to know if any TDS % is entered
  const hasTdsPercent = useMemo(() => {
    const f = (form.firm_tds_percent || "").trim();
    const i = (form.individual_tds_percent || "").trim();
    const w = (form.without_pan_tds_percent || "").trim();
    return !!(f || i || w);
  }, [
    form.firm_tds_percent,
    form.individual_tds_percent,
    form.without_pan_tds_percent,
  ]);

  // ✅ NEW (minimal): robust "isEdit" so editId=0/"0"/null doesn't break conditions
  const isEditMode = useMemo(() => {
    const n = Number(editId);
    return Number.isFinite(n) && n > 0;
  }, [editId]);

  // EDIT MODE: pre-fill from state
  useEffect(() => {
    if (routeLocation.state?.tdsData) {
      const d = routeLocation.state.tdsData;

      setEditId(d.tds_id);

      const eff = d.effective_date ? d.effective_date.substring(0, 10) : "";
      setForm({
        account_code: d.account_code || "",
        effective_date: eff,
        firm_tds_percent: toFixedOrEmpty(d.firm_tds_percent, 3),
        individual_tds_percent: toFixedOrEmpty(d.individual_tds_percent, 3),
        without_pan_tds_percent: toFixedOrEmpty(d.without_pan_tds_percent, 3),
        interest_percent: toFixedOrEmpty(d.interest_percent, 3),
        single_limit_amount: toFixedOrEmpty(d.single_limit_amount, 2),
        aggregate_limit_amount: toFixedOrEmpty(d.aggregate_limit_amount, 2),
      });

      setSelectedAccountMasterId(d.account_master_id || null);

      // ✅ NEW (minimal): capture original values for comparison
      originalEffectiveDateRef.current = eff;
      originalAccountMasterIdRef.current = d.account_master_id || null;

      window.history.replaceState({}, document.title);
    }
  }, [routeLocation]);

  // clear Interest % automatically if no TDS % is defined
  useEffect(() => {
    if (!hasTdsPercent && form.interest_percent) {
      setForm((p) => ({ ...p, interest_percent: "" }));
    }
  }, [hasTdsPercent, form.interest_percent]);

  // Load Account Code options
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const TDS_ACCOUNTTYPE_ID = 1;

        const res = await axios.post(
          `${baseUrl}/finance/get_accountcode_byaccounttype_id`,
          { accounttype_id: TDS_ACCOUNTTYPE_ID },
          { headers: { "Content-Type": "application/json" } }
        );
        setAccountOptions(res.data || []);
      } catch (err) {
        console.error("Error loading account codes:", err);
        toast.error("Failed to load account codes");
      }
    };

    loadAccounts();
  }, [baseUrl]);

  const handlePercent = (key) => (e) => {
    const v = e.target.value.trim();
    if (v === "" || percentPattern.test(v)) {
      setForm((p) => ({ ...p, [key]: v }));
    }
  };

  const handleAmount = (key) => (e) => {
    const v = e.target.value.trim();
    if (v === "" || amountPattern.test(v)) {
      setForm((p) => ({ ...p, [key]: v }));
    }
  };

  const resetForm = () => {
    setForm({
      account_code: "",
      effective_date: "",
      firm_tds_percent: "",
      individual_tds_percent: "",
      without_pan_tds_percent: "",
      interest_percent: "",
      single_limit_amount: "",
      aggregate_limit_amount: "",
    });
    setSelectedAccountMasterId(null);
    setEditId(null);

    // ✅ NEW: reset originals too
    originalEffectiveDateRef.current = "";
    originalAccountMasterIdRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.account_code) {
      toast.error("Account Code is required");
      return;
    }
    if (!form.effective_date) {
      toast.error("Effective Date is required");
      return;
    }

    // resolve account_master_id
    let accountMasterId = selectedAccountMasterId;
    if (!accountMasterId) {
      const opt = accountSelectOptions.find((o) => o.value === form.account_code);
      accountMasterId = opt?.account_master_id || null;
    }
    if (!accountMasterId) {
      toast.error("Unable to resolve Account Master. Please re-select account.");
      return;
    }

    // business validations (unchanged)
    const numOrNull = (s) => (s === "" ? null : Number(s));

    const firmVal = numOrNull(form.firm_tds_percent);
    const individualVal = numOrNull(form.individual_tds_percent);
    const withoutPanVal = numOrNull(form.without_pan_tds_percent);
    const interestVal = numOrNull(form.interest_percent);
    const singleAmt = numOrNull(form.single_limit_amount);
    const aggregateAmt = numOrNull(form.aggregate_limit_amount);

    if (
      [firmVal, individualVal, withoutPanVal, interestVal, singleAmt, aggregateAmt]
        .some((v) => v !== null && Number.isNaN(v))
    ) {
      toast.error("One or more numeric fields have invalid values.");
      return;
    }

    if (firmVal !== null && firmVal <= 0) {
      toast.error("Firm TDS % must be greater than 0.");
      return;
    }
    if (individualVal !== null && individualVal <= 0) {
      toast.error("Individual TDS % must be greater than 0.");
      return;
    }
    if (withoutPanVal !== null && withoutPanVal <= 0) {
      toast.error("Without PAN TDS % must be greater than 0.");
      return;
    }

    const MAX_AMOUNT = 9999999.99;

    if (singleAmt !== null) {
      if (singleAmt < 0 || singleAmt > MAX_AMOUNT) {
        toast.error("Single Limit Amount must be between 0 and 99,99,999.99.");
        return;
      }
    }

    if (aggregateAmt !== null) {
      if (aggregateAmt < 0 || aggregateAmt > MAX_AMOUNT) {
        toast.error("Aggregate Limit Amount must be between 0 and 99,99,999.99.");
        return;
      }
    }

    if (singleAmt !== null && aggregateAmt !== null && singleAmt > aggregateAmt) {
      toast.error("Aggregate Limit Amount should be greater than or equal to Single Limit Amount.");
      return;
    }

    if (firmVal !== null && individualVal !== null && individualVal < firmVal) {
      toast.error("Individual TDS % should be greater than or equal to Firm TDS %.");
      return;
    }

    if (individualVal !== null && withoutPanVal !== null && withoutPanVal < individualVal) {
      toast.error("Without PAN TDS % should be greater than or equal to Individual TDS %.");
      return;
    }

    if (interestVal !== null) {
      if (interestVal <= 0) {
        toast.error("Interest % must be greater than 0.");
        return;
      }
      if (interestVal < 0.001 || interestVal > 100) {
        toast.error("Interest % must be between 0.001 and 100.000.");
        return;
      }
    }

    const hasAnyTdsValue =
      firmVal !== null || individualVal !== null || withoutPanVal !== null;

    if (!hasAnyTdsValue && interestVal !== null) {
      toast.error("Interest % can be defined only when at least one TDS % is specified.");
      return;
    }

    const num = (s) => (s === "" ? null : Number(s));

    try {
      const headers = { "Content-Type": "application/json" };

      // ✅ FIX #1:
      // In EDIT mode, DO NOT call check_effective_date at all.
      // That endpoint is what typically forces "must be current/latest date".
      if (!isEditMode) {
        const effRes = await axios.post(
          `${baseUrl}/finance/check_effective_date`,
          { flag: "TDS" },
          { headers }
        );

        const lastEffective = effRes.data?.effective_date;
        if (lastEffective) {
          const selected = new Date(form.effective_date);
          const last = new Date(lastEffective);
          if (selected < last) {
            const lastDisplay = new Date(lastEffective).toLocaleDateString("en-GB");
            toast.error(`Effective Date cannot be earlier than existing effective date ${lastDisplay}`);
            return;
          }
        }
      }

      // ✅ FIX #2:
      // In EDIT, skip duplicate check ONLY when (Account + Effective Date) did not change.
      const originalEff = originalEffectiveDateRef.current || "";
      const originalAccId = originalAccountMasterIdRef.current;

      const accountChanged = Number(accountMasterId) !== Number(originalAccId);
      const dateChanged = (form.effective_date || "") !== (originalEff || "");

      const shouldRunDupCheck = !isEditMode || accountChanged || dateChanged;

      if (shouldRunDupCheck) {
        const dupRes = await axios.post(
          `${baseUrl}/finance/check_duplication_effective_date_account_id`,
          {
            code_type_id: accountMasterId,
            hsn_sac_master_id: 0,
            gst_type_id: 0,
            flag: "TDS",
            effective_date: form.effective_date,
            id: isEditMode ? Number(editId) : 0, // ✅ ensure correct id type
          },
          { headers }
        );

        if (dupRes.data && typeof dupRes.data.result !== "undefined") {
          if (dupRes.data.result === 1) {
            toast.error("A TDS record already exists for this Account Code & Effective Date");
            return;
          }
        }
      }

      // final submit (unchanged)
      const payload = {
        tds_id: isEditMode ? Number(editId) : 0,
        account_master_id: accountMasterId,
        effect_date: form.effective_date,
        firm_tds_percent: num(form.firm_tds_percent),
        individual_tds_percent: num(form.individual_tds_percent),
        without_pan_tds_percentage: num(form.without_pan_tds_percent),
        single_limit_amount: num(form.single_limit_amount),
        aggregate_limit_amount: num(form.aggregate_limit_amount),
        interest_percentage: num(form.interest_percent),
        is_active: true,
        created_by: userObj.user_name || userObj.login_id || "SYSTEM",
      };

      await axios.post(`${baseUrl}/finance/addupdatetds_master`, payload, { headers });

      toast.success(isEditMode ? "Record updated successfully" : "Record added successfully");
      resetForm();
    } catch (error) {
      console.error("Error in TDS submit:", error);
      const status = error?.response?.status;
      if (status === 500) toast.error("Server error while submitting data");
      else toast.error("Failed to submit data");
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    {isEditMode ? "Edit TDS Master" : "Add TDS Master"}
                  </h6>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Account Code <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        inputId="account_code"
                        styles={selectStyles}
                        options={accountSelectOptions}
                        isClearable
                        isSearchable
                        placeholder="Select Account Code"
                        filterOption={accountFilter}
                        value={
                          accountSelectOptions.find((o) => o.value === form.account_code) || null
                        }
                        onChange={(opt) => {
                          setForm((p) => ({ ...p, account_code: opt ? opt.value : "" }));
                          setSelectedAccountMasterId(opt ? opt.account_master_id : null);
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Effective Date <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        value={form.effective_date}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, effective_date: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Firm TDS %</Form.Label>
                      <Form.Control
                        placeholder="0.000"
                        inputMode="decimal"
                        value={form.firm_tds_percent}
                        onChange={handlePercent("firm_tds_percent")}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Individual TDS %</Form.Label>
                      <Form.Control
                        placeholder="0.000"
                        inputMode="decimal"
                        value={form.individual_tds_percent}
                        onChange={handlePercent("individual_tds_percent")}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Without PAN TDS %</Form.Label>
                      <Form.Control
                        placeholder="0.000"
                        inputMode="decimal"
                        value={form.without_pan_tds_percent}
                        onChange={handlePercent("without_pan_tds_percent")}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Single Limit Amount</Form.Label>
                      <Form.Control
                        placeholder="0.00"
                        inputMode="decimal"
                        value={form.single_limit_amount}
                        onChange={handleAmount("single_limit_amount")}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Aggregate Limit Amount</Form.Label>
                      <Form.Control
                        placeholder="0.00"
                        inputMode="decimal"
                        value={form.aggregate_limit_amount}
                        onChange={handleAmount("aggregate_limit_amount")}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Interest %</Form.Label>
                      <Form.Control
                        placeholder="0.000"
                        inputMode="decimal"
                        value={form.interest_percent}
                        onChange={handlePercent("interest_percent")}
                        disabled={!hasTdsPercent}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button type="button" className="btn btn-info clearBtn" onClick={resetForm}>
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="success" className="btn btn-success primaryBtn">
                    Submit
                  </Button>
                </div>
              </div>

            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddTDSMaster;
